import { useEffect, useState } from "react";
import { History } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { BM_COLS, CLASS_COLS } from "@/components/settings/ClassTablesEditor";

const SECTIONS = { rules: "Selection rules", class_tables: "Class table", matrices: "Rating rule", formula: "Price formula",
  letter_scores: "Class A–E score", tracks: "Track list" };
const label = (path) => path.split(" › ").map((p, i) => (i === 0 ? SECTIONS[p] || p : p
  .replace(/^class\[(\d+)\]$/, (_, n) => `Class ${CLASS_COLS[n - 1] || n}`)
  .replace(/^bm\[(\d+)\]$/, (_, n) => `Benchmark ${BM_COLS[n - 1] || n}`)
  .replace(/^grid\[(\d+)\]\[(\d+)\]$/, (_, r, c) => `points: ${r - 1} wins/places × ${c} starts`)
  .replace(/_/g, " "))).join(" › ");

const show = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const s = typeof v === "object" ? JSON.stringify(v) : String(v);
  return s.length > 70 ? `${s.slice(0, 67)}...` : s;
};
const when = (iso) => new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Brisbane", weekday: "short", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });

const Entry = ({ e, i }) => (
  <div className="panel p-4 space-y-2" data-testid={`history-entry-${i}`}>
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
      <span className="num text-white">{when(e.at)} AEST</span>
      <span className="px-2 py-0.5 rounded border border-slate-700 font-mono uppercase text-[10px] text-slate-300">{e.source}</span>
      <span className="text-slate-500">{e.email} • {e.total} change{e.total === 1 ? "" : "s"}{e.total > e.changes.length ? ` (first ${e.changes.length} shown)` : ""}</span>
    </div>
    <table className="w-full text-xs">
      <tbody>
        {e.changes.map((c, j) => (
          <tr key={j} className="border-t border-slate-800/70" data-testid={`history-change-${i}-${j}`}>
            <td className="py-1.5 pr-4 text-slate-300">{label(c.path)}</td>
            <td className="py-1.5 pr-2 num text-rose-300/80 line-through whitespace-nowrap">{show(c.old)}</td>
            <td className="py-1.5 num text-emerald-300 whitespace-nowrap">→ {show(c.new)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const SettingsHistory = () => {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/admin/settings-history").then((r) => setItems(r.data)).catch((e) => setErr(formatApiError(e))); }, []);
  if (err) return <p className="text-sm text-rose-300">{err}</p>;
  if (!items) return <div className="loader-bar" />;
  return (
    <div className="space-y-3" data-testid="settings-history">
      <div className="eyebrow flex items-center gap-2"><History className="h-3.5 w-3.5" />Every saved change, newest first • times in AEST • last 200 saves</div>
      {items.length ? items.map((e, i) => <Entry key={`${e.at}-${i}`} e={e} i={i} />)
        : <p className="text-sm text-slate-500" data-testid="settings-history-empty">No changes recorded yet. Changes you save from now on appear here.</p>}
    </div>
  );
};
